import { PageLayout } from '@/components/feature/PageLayout';
import { Phone, MessageCircle, MapPin, Clock, Mail } from 'lucide-react';

const seo = {
  title: 'Contact — Devis gratuit réparation volet roulant Charleroi',
  description: 'Contactez-nous pour un devis gratuit de réparation de volet roulant à Charleroi. Téléphone 0800 20 410, WhatsApp, email. Intervention dans tout le Hainaut. Diagnostic gratuit.',
  keywords: 'contact réparation volet roulant Charleroi, devis gratuit volet roulant, téléphone réparateur volet, dépannage volet Charleroi contact',
  canonicalUrl: '/contact/',
};

function Hero() {
  return (
    <section className="relative min-h-[350px] md:min-h-[420px] flex items-center overflow-hidden">
      <img
        src="/images/hero-contact.webp"
        alt="Contactez Volet Charleroi pour un devis gratuit"
        className="absolute inset-0 w-full h-full object-cover object-top"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/85 to-secondary-500/70" />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-2xl">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">
            Contactez-nous
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/85">
            Un problème avec votre volet roulant ? Demandez votre devis gratuit
          </p>
        </div>
      </div>
    </section>
  );
}

export default function ContactPage() {
  return (
    <PageLayout seo={seo} showDevisForm={false}>
      <Hero />

      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Contact info */}
            <div className="lg:col-span-1 space-y-6">
              <h2 className="text-2xl font-heading font-bold text-foreground-700 mb-6">
                Nos coordonnées
              </h2>

              <div className="space-y-5">
                <a
                  href="tel:+3280020410"
                  className="flex items-start gap-4 p-4 bg-white border border-background-200 rounded-xl hover:border-accent-300 transition-all group"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center shrink-0 group-hover:bg-accent-100 transition-colors">
                    <Phone className="w-5 h-5 text-accent-500" />
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-0.5">Téléphone gratuit</p>
                    <p className="text-base font-semibold text-foreground-700 whitespace-nowrap">0800 20 410</p>
                  </div>
                </a>

                <a
                  href="https://wa.me/32475585219"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-4 bg-white border border-background-200 rounded-xl hover:border-success-300 transition-all group"
                >
                  <div className="w-10 h-10 rounded-lg bg-success-50 flex items-center justify-center shrink-0 group-hover:bg-success-100 transition-colors">
                    <MessageCircle className="w-5 h-5 text-success-500" />
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-0.5">WhatsApp</p>
                    <p className="text-base font-semibold text-foreground-700 whitespace-nowrap">+32 475 58 52 19</p>
                  </div>
                </a>

                <a
                  href="mailto:info@volet-charleroi.be"
                  className="flex items-start gap-4 p-4 bg-white border border-background-200 rounded-xl hover:border-secondary-300 transition-all group"
                >
                  <div className="w-10 h-10 rounded-lg bg-secondary-50 flex items-center justify-center shrink-0 group-hover:bg-secondary-100 transition-colors">
                    <Mail className="w-5 h-5 text-secondary-500" />
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-0.5">Email</p>
                    <p className="text-base font-semibold text-foreground-700 break-all">info@volet-charleroi.be</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4 bg-white border border-background-200 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-0.5">Adresse</p>
                    <p className="text-base font-semibold text-foreground-700 whitespace-nowrap">6000 Charleroi</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white border border-background-200 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-0.5">Horaires</p>
                    <p className="text-sm font-medium text-foreground-600">Lun-Jeu : 8h-19h</p>
                    <p className="text-sm font-medium text-foreground-600">Ven & Sam : 8h-12h</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Devis Form */}
            <div className="lg:col-span-2">
              <div className="card border border-background-200 p-6 md:p-8">
                <h2 className="text-xl font-heading font-bold text-foreground-700 mb-6">
                  Demandez votre devis gratuit
                </h2>

                <form
                  action="https://formspree.io/f/mykqvjoz"
                  method="POST"
                  data-readdy-form
                >
                  <input
                    type="text"
                    name="_gotcha"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <input type="hidden" name="_next" value="https://volet-charleroi.be/merci/" />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-nom" className="block text-sm font-medium text-foreground-600 mb-1.5">
                        Nom complet <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-nom"
                        name="nom"
                        required
                        className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                        placeholder="Votre nom"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-telephone" className="block text-sm font-medium text-foreground-600 mb-1.5">
                        Téléphone <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="contact-telephone"
                        name="telephone"
                        required
                        className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                        placeholder="04XX XX XX XX"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-600 mb-1.5">
                        Email
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                        placeholder="votre@email.be"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-commune" className="block text-sm font-medium text-foreground-600 mb-1.5">
                        Commune <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-commune"
                        name="commune"
                        required
                        className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                        placeholder="Votre commune"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="contact-panne" className="block text-sm font-medium text-foreground-600 mb-1.5">
                      Type de panne <span className="text-accent-500">*</span>
                    </label>
                    <select
                      id="contact-panne"
                      name="type_panne"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors bg-white"
                    >
                      <option value="">Sélectionnez un type de panne</option>
                      <option value="Volet bloqué">Volet bloqué</option>
                      <option value="Ne remonte plus">Ne remonte plus</option>
                      <option value="Moteur HS">Moteur HS</option>
                      <option value="Sangle ou manivelle cassée">Sangle ou manivelle cassée</option>
                      <option value="Télécommande HS">Télécommande HS</option>
                      <option value="Lames ou tablier abîmés">Lames ou tablier abîmés</option>
                      <option value="Motorisation">Motorisation</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-600 mb-1.5">
                      Décrivez votre problème
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors resize-none"
                      placeholder="Décrivez votre panne, le type de volet, la marque si vous la connaissez..."
                    />
                    <p className="text-xs text-foreground-400 mt-1">Maximum 500 caractères</p>
                  </div>

                  <div className="mb-6">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="consentement_rgpd"
                        required
                        className="mt-0.5 w-4 h-4 text-accent-500 border-background-300 rounded focus:ring-accent-400 shrink-0"
                      />
                      <span className="text-sm text-foreground-600">
                        J'accepte que mes données soient collectées pour le traitement de ma demande.{' '}
                        <Link to="/politique-confidentialite/" className="text-accent-500 hover:text-accent-600 underline whitespace-nowrap">
                          Politique de confidentialité
                        </Link>
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="btn-primary w-full sm:w-auto px-8 py-3 text-base"
                  >
                    Envoyer ma demande
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}